// Pixel↔cell geometry for the rendered SortableGrid. Pure DOM math — no React.
// Gesture hooks measure once at gesture start (metrics) and convert pointer
// deltas to cell steps through these helpers.

import type { GridMetrics } from './grid-layout';

/** Center-to-center horizontal distance between adjacent columns. */
export function pitchX(metrics: GridMetrics): number {
  return metrics.colWidth + metrics.gapX;
}

/** Center-to-center vertical distance between adjacent rows. */
export function pitchY(metrics: GridMetrics): number {
  return metrics.rowHeight + metrics.gapY;
}

/** Snap a grid-content-space point (pixels from the grid's top-left) to the
 *  cell it falls in: `floor(p / pitch)` clamped to `[0, columns-1]` /
 *  `[0, maxRows-1]`. Points inside a gutter map to the cell above/left of it
 *  (the gap trails its cell in pitch space). */
export function pointToCell(
  metrics: GridMetrics,
  columns: number,
  maxRows: number,
  p: { x: number; y: number },
): { x: number; y: number } {
  // Degenerate pitch (an unmeasured/zero-size grid) → cell 0 on that axis,
  // mirroring `rowCapacity`'s guard — `0 / 0` would emit a NaN cell.
  const px = pitchX(metrics);
  const py = pitchY(metrics);
  const x =
    px <= 0
      ? 0
      : Math.min(Math.max(0, Math.floor(p.x / px)), Math.max(0, columns - 1));
  const y =
    py <= 0
      ? 0
      : Math.min(Math.max(0, Math.floor(p.y / py)), Math.max(0, maxRows - 1));
  return { x, y };
}

/** Whole rows covered by a pixel height (`floor((px + gapY) / pitchY)`).
 *  Geometry measurement ONLY — used once at gesture start to size the row
 *  floor; never in domain height math (heights are defined in rows). */
export function rowCapacity(heightPx: number, metrics: GridMetrics): number {
  const pitch = pitchY(metrics);
  if (pitch <= 0) return 0;
  return Math.max(0, Math.floor((heightPx + metrics.gapY) / pitch));
}

/** Rendered pixel width of a `w`-column tile (columns + interior gutters). */
export function tileWidthPx(metrics: GridMetrics, w: number): number {
  return w * metrics.colWidth + (w - 1) * metrics.gapX;
}

/** Rendered pixel height of an `h`-row tile (rows + interior gutters). */
export function tileHeightPx(metrics: GridMetrics, h: number): number {
  return h * metrics.rowHeight + (h - 1) * metrics.gapY;
}

/** Total rendered grid width in pixels. */
export function gridWidthPx(metrics: GridMetrics, columns: number): number {
  return columns * metrics.colWidth + (columns - 1) * metrics.gapX;
}

/** Measure a rendered grid element into cell metrics. Reads computed styles, so
 *  call at gesture start (not per pointermove). */
export function measureGrid(
  el: HTMLElement,
  columns: number,
  rowHeight: number,
): GridMetrics {
  const rect = el.getBoundingClientRect();
  const styles = window.getComputedStyle(el);
  const gapX = parseFloat(styles.columnGap || styles.gap || '0') || 0;
  const gapY = parseFloat(styles.rowGap || styles.gap || '0') || 0;
  const colWidth = (rect.width - gapX * (columns - 1)) / columns;
  const autoRows = parseFloat(styles.gridAutoRows) || rowHeight;
  return { colWidth, rowHeight: autoRows, gapX, gapY };
}

/** First ancestor that vertically scrolls (computed overflow-y auto/scroll/
 *  overlay), falling back to the document's scrolling element. Shared by the
 *  resize auto-scroll loop and anything else that needs the scroll viewport. */
export function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
}

/** Viewport point where a dnd-kit drag was activated. dnd-kit's move events
 *  only carry the delta; the absolute pointer is activator + delta. */
export function activatorPoint(event: {
  activatorEvent: Event;
}): { x: number; y: number } | null {
  const activator = event.activatorEvent as Partial<MouseEvent> &
    Partial<TouchEvent>;
  const touch = activator.touches?.[0];
  if (touch && typeof touch.clientX === 'number') {
    return { x: touch.clientX, y: touch.clientY };
  }
  if (typeof activator.clientX === 'number' && typeof activator.clientY === 'number') {
    return { x: activator.clientX, y: activator.clientY };
  }
  return null;
}
